//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import SearchBar from '@components/molecules/search-bar/SearchBar';
import { TrackingIdPrefix } from '@models/TrackingId';
import { getFuzzySearch } from '@utils/getFuzzySearch';

export interface DropdownSelectSearchConfig {
  placeholder: string;
  visuallyHiddenLabel: string;
  threshold?: number;
  trackingIdPrefix: TrackingIdPrefix;
}

export interface DropdownSelectSearchProps<T> {
  className?: string;
  handleClear?: () => void;
  handleSearch: (searchTerm: string, filteredItems: T[]) => void;
  itemsToSearch: T[];
  searchBarConfig: DropdownSelectSearchConfig;
  searchKeys: string[];
  searchValue: string;
  setSearchValue: (searchValue: string) => void;
}

export const DropdownSelectSearch = <T,>({
  className,
  handleClear,
  handleSearch,
  itemsToSearch,
  searchBarConfig,
  searchKeys,
  searchValue,
  setSearchValue,
}: DropdownSelectSearchProps<T>) => {
  const handleChange = (searchTerm: string) => {
    setSearchValue(searchTerm);
    const filteredItems = getFuzzySearch({
      itemsToSearch,
      searchKeys,
      searchTerm,
      threshold: searchBarConfig.threshold,
    });
    handleSearch(searchTerm, filteredItems);
  };

  const resetSearch = () => {
    setSearchValue('');
    if (handleClear) {
      handleClear();
    }
  };

  // DropdownMenu will automatically focus first option that start with the inserted key, but in the case of search, we want to handle it ourselves.
  return (
    <SearchBarContainer onKeyDown={(event) => event.stopPropagation()}>
      <SearchBar
        {...searchBarConfig}
        className={className}
        onChange={handleChange}
        value={searchValue}
        handleClear={handleClear ? resetSearch : undefined}
      />
    </SearchBarContainer>
  );
};

const SearchBarContainer = styled.div(
  ({ theme }) => `
    padding: ${theme.spacings[2]} calc(${theme.spacings[2]} + ${theme.spacings[1]});
  `,
);
